import { LayoutModule } from '@angular/cdk/layout';
import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule, NgZone } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouteReuseStrategy } from '@angular/router';
import { MainLoaderModule } from '@core/components/main-loader/main-loader.module';
import { ErrorHttpInterceptor } from '@core/interceptors/error-http-interceptor.service';
import { ChatService } from '@core/services/chat.service';
import { DeviceInfoService } from '@core/services/device-info.service';
import { SessionService } from '@core/services/session.service';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { Keyboard } from '@ionic-native/keyboard/ngx';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRouting } from './app.routing';
import { ChatState } from './home/chat/store/chat.state';
registerLocaleData(localeEs, 'es');
export function initializeApp(sessionService: SessionService, chatService: ChatService, _router: Router, ngZone: NgZone, deviceInfoService: DeviceInfoService) {
  return async () => {
    try {
      sessionService.setLoading(true);
      const session = await sessionService.loadSession(false);
      if (session && session.sid) {
        await deviceInfoService.startSendDeviceInfo();
        chatService.connect(session);
        sessionService.loadBindingUnit();
        if (environment.production) {
          ngZone.run(() => _router.navigate(['/home']));
        }
      } else {
        const adminSession = await sessionService.loadAdminSession(false);
        if (adminSession && adminSession.sid) {
          ngZone.run(() => _router.navigate(['/drivers']));
        } else {
          ngZone.run(() => _router.navigate(['/login']));
        }
      }
    } catch (error) {
      console.error('Error initializing app:', error);
    } finally {
      sessionService.setLoading(false);
    }
  };
}
@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    AppRouting,
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    NgxsModule.forRoot([ChatState], { developmentMode: !environment.production }),
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
    IonicModule.forRoot({ mode: 'md' }),
    LayoutModule,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    FormsModule,
    PdfViewerModule,
    MainLoaderModule,
    ReactiveFormsModule,
  ],
  providers: [
    Keyboard,
    AppVersion,
    {
      provide: APP_INITIALIZER,
      useFactory: (sessionService: SessionService, chatService: ChatService, _router: Router, ngZone: NgZone, deviceInfoService: DeviceInfoService) => initializeApp(sessionService, chatService, _router, ngZone, deviceInfoService),
      deps: [SessionService, ChatService, Router, NgZone, DeviceInfoService],
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: LOCALE_ID, useValue: 'es-Es' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHttpInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    DatePipe,
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
})
export class AppModule {
}
